/**
 * Rectangle Component
 * 
 * A simplified wrapper for rendering 2D rectangles with fixed optimal settings.
 * 
 * Only accepts dimension values (for labels), not positions.
 * All positioning and styling are predefined for consistency.
 * 
 * Uses SVG for rendering, similar to polygon rendering in GraphRenderer.
 */

import React from 'react';
import { Box } from '@mui/material';

interface RectangleProps {
  length?: number; // Length dimension (for label display)
  width?: number; // Width dimension (for label display)
  unit?: string; // Unit of measurement (default: 'cm')
  color?: string; // Fill color (default: '#e0e7ff')
  strokeColor?: string; // Stroke color (default: '#4f46e5')
}

/**
 * Rectangle Component
 * 
 * Renders a 2D rectangle with fixed, optimal positioning and labeling.
 * The rectangle is always rendered at a normalized size for visual consistency.
 * The actual dimension values are only displayed in the labels.
 * 
 * @param length - Length dimension (default: 8)
 * @param width - Width dimension (default: 5)
 * @param unit - Unit of measurement (default: 'cm')
 * @param color - Fill color in any CSS format (default: '#e0e7ff')
 * @param strokeColor - Stroke color in any CSS format (default: '#4f46e5')
 */
export const Rectangle: React.FC<RectangleProps> = ({
  length = 8,
  width = 5,
  unit = 'cm',
  color = '#e0e7ff',
  strokeColor = '#4f46e5'
}) => {
  // Fixed normalized size for visual consistency
  // Actual dimension values are only shown in labels
  const normalizedLength = 40; // Length in SVG units (viewBox is 0 0 100 100)
  const normalizedWidth = 25; // Width in SVG units
  
  // Center the rectangle in the viewBox
  const centerX = 50;
  const centerY = 50;
  const halfLength = normalizedLength / 2;
  const halfWidth = normalizedWidth / 2;
  
  // Rectangle corners
  const x1 = centerX - halfLength;
  const y1 = centerY - halfWidth;
  const x2 = centerX + halfLength;
  const y2 = centerY + halfWidth;
  
  // Label positions
  const lengthLabelX = centerX;
  const lengthLabelY = centerY - halfWidth - 5; // Above the rectangle
  const widthLabelX = centerX + halfLength + 5; // To the right of the rectangle
  const widthLabelY = centerY;
  
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', maxWidth: '320px', height: '320px', display: 'block' }}
      >
        {/* Rectangle fill */}
        <rect
          x={x1}
          y={y1}
          width={normalizedLength}
          height={normalizedWidth}
          fill={color}
          opacity={0.4}
        />
        
        {/* Rectangle outline */}
        <rect
          x={x1}
          y={y1}
          width={normalizedLength}
          height={normalizedWidth}
          fill="none"
          stroke={strokeColor}
          strokeWidth={1.5}
          strokeLinejoin="round"
        />
        
        {/* Corner points */}
        <circle cx={x1} cy={y1} r={1.2} fill={strokeColor} />
        <circle cx={x2} cy={y1} r={1.2} fill={strokeColor} />
        <circle cx={x2} cy={y2} r={1.2} fill={strokeColor} />
        <circle cx={x1} cy={y2} r={1.2} fill={strokeColor} />
        
        {/* Length label (top) */}
        <text
          x={lengthLabelX}
          y={lengthLabelY}
          fontSize={4.5}
          fill="#374151"
          textAnchor="middle"
          fontWeight={600}
        >
          l = {length} {unit}
        </text>
        
        {/* Width label (right) */}
        <text
          x={widthLabelX}
          y={widthLabelY}
          fontSize={4.5}
          fill="#374151"
          textAnchor="start"
          fontWeight={600}
        >
          w = {width} {unit}
        </text>
      </svg>
    </Box>
  );
};

export default Rectangle;
